'use client';

import { useEffect, useRef, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { Globe, Building2, Users, MessageCircle, Briefcase } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

export default function WhyIstanbulSection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const sectionRef = useRef<HTMLElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const features = useMemo(() => [
        {
            icon: <Globe className="w-6 h-6 text-gold" />,
            text: localizeText(t('why_istanbul.features.standards'))
        },
        {
            icon: <Building2 className="w-6 h-6 text-gold" />,
            text: localizeText(t('why_istanbul.features.clinics'))
        },
        {
            icon: <Users className="w-6 h-6 text-gold" />,
            text: localizeText(t('why_istanbul.features.specialists'))
        },
        {
            icon: <MessageCircle className="w-6 h-6 text-gold" />,
            text: localizeText(t('why_istanbul.features.support'))
        },
        {
            icon: <Briefcase className="w-6 h-6 text-gold" />,
            text: localizeText(t('why_istanbul.features.packages'))
        }
    ], [t, localizeText]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(contentRef.current,
                { opacity: 0, x: isRTL ? 50 : -50 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 70%",
                        end: "bottom 30%",
                        toggleActions: "play none none reverse"
                    }
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, [isRTL]);

    return (
        <section
            id="why-istanbul"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            <div className="container">
                <div className={cn("grid lg:grid-cols-2 gap-12 items-center", isRTL && "lg:grid-flow-col-dense")}>
                    {/* Left: Image */}
                    <div className={cn("relative", isRTL && "lg:col-start-2")}>
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                            <OptimizedImage
                                src="/images/env/istanbul.webp"
                                alt={localizeText(t('why_istanbul.image_alt'))}
                                width={600}
                                height={400}
                                className="w-full h-[400px] object-cover"
                            />
                        </div>
                    </div>

                    {/* Right: Content */}
                    <div ref={contentRef} className={cn("space-y-8", isRTL && "lg:col-start-1")}>
                        <div>
                            <h2 className={cn("text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif", isRTL && "font-arabic text-right")}>
                                {localizeText(t('why_istanbul.title_part1'))}{' '}
                                <span className="text-gold">{localizeText(t('why_istanbul.title_part2'))}</span>
                            </h2>
                            <p className={cn("text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8", isRTL && "font-arabic text-right")}>
                                {localizeText(t('why_istanbul.description_1'))}
                            </p>
                        </div>

                        {/* Features List */}
                        <div className="space-y-4">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        "flex items-center p-4 rounded-xl bg-white dark:bg-slate-800 shadow-xs dark:border dark:border-slate-700 hover:shadow-md transition-all duration-300",
                                        isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                    )}
                                >
                                    <div className="p-2 rounded-lg bg-gold/10">
                                        {feature.icon}
                                    </div>
                                    <span className={cn("text-navy dark:text-white font-medium", isRTL && "font-arabic text-right")}>
                                        {feature.text}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="pt-4">
                            <p className={cn("text-lg text-slate-600 dark:text-slate-300 leading-relaxed", isRTL && "font-arabic text-right")}>
                                {localizeText(t('why_istanbul.description_2'))}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 
